import { Badge, Box, HStack, Input, List, ListItem, Text } from '@chakra-ui/react';
import { useMemo, useState } from 'react';
import { SETTINGS_BORDER_COLOR } from '../../../../components/settings/primitives';
import type { OemOption } from './oemDraft';

export interface OemMakeSearchFieldProps {
    options: OemOption[];
    /** Makes already configured, flagged in the results. */
    addedMakeIds: Array<string>;
    onSelect: (option: OemOption) => void;
    autoFocus?: boolean;
}

/**
 * Make lookup for the OEM screen. The list of makes ships with the settings
 * response rather than coming from a search endpoint, so this filters in memory
 * — but it reads like the carrier and vendor lookups, and makes already
 * configured are flagged instead of hidden, so it is clear why a familiar name
 * cannot be added twice.
 */
const OemMakeSearchField = ({
    options,
    addedMakeIds,
    onSelect,
    autoFocus = false,
}: OemMakeSearchFieldProps) => {
    const [searchTerm, setSearchTerm] = useState('');
    const [showResults, setShowResults] = useState(false);

    const added = useMemo(() => new Set(addedMakeIds), [addedMakeIds]);
    const results = useMemo(() => {
        const query = searchTerm.trim().toLowerCase();
        return options
            .filter((option) => option.name?.trim())
            .filter((option) => option.name.toLowerCase().includes(query))
            .slice(0, 50);
    }, [options, searchTerm]);

    return (
        <Box position="relative">
            <Input
                size="sm"
                h="36px"
                bg="white"
                autoFocus={autoFocus}
                value={searchTerm}
                placeholder="Search makes..."
                onChange={(e) => {
                    setSearchTerm(e.target.value);
                    setShowResults(true);
                }}
                onFocus={() => setShowResults(true)}
                // Deferred so a click on a result lands before the list closes.
                onBlur={() => setTimeout(() => setShowResults(false), 150)}
            />
            {showResults && (
                <Box
                    position="absolute"
                    top="100%"
                    left={0}
                    right={0}
                    mt={1}
                    bg="white"
                    borderWidth="1px"
                    borderColor={SETTINGS_BORDER_COLOR}
                    borderRadius="md"
                    // Overlay chrome per Yaxle: hairline border, no drop shadow.
                    boxShadow="none"
                    zIndex={20}
                    maxH="260px"
                    overflowY="auto"
                >
                    {results.length === 0 ? (
                        <Box p={4}>
                            <Text fontSize="sm" color="gray.500">
                                No makes found.
                            </Text>
                        </Box>
                    ) : (
                        <List spacing={0}>
                            {results.map((option) => {
                                const isAdded = added.has(option._id);
                                return (
                                    <ListItem
                                        key={option._id}
                                        px={4}
                                        py={2}
                                        cursor="pointer"
                                        _hover={{ bg: 'gray.50' }}
                                        onClick={() => {
                                            onSelect(option);
                                            setSearchTerm('');
                                            setShowResults(false);
                                        }}
                                    >
                                        <HStack spacing={2}>
                                            <Text
                                                fontSize="sm"
                                                color={isAdded ? 'gray.400' : 'gray.800'}
                                            >
                                                {option.name}
                                            </Text>
                                            {isAdded && (
                                                <Badge colorScheme="gray" fontSize="2xs">
                                                    Already added
                                                </Badge>
                                            )}
                                        </HStack>
                                    </ListItem>
                                );
                            })}
                        </List>
                    )}
                </Box>
            )}
        </Box>
    );
};

export default OemMakeSearchField;
